import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { supabase, type User } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, username: string, displayName?: string) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  sendVerificationEmail: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // 直接设置 session 为有效状态
    supabase.auth.getSession().then(({ data: { session } }: any) => {
      if (session?.user) {
        setUser(session.user as User)
        fetchUserProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      if (session?.user) {
        console.log('Auth state changed: user logged in')
        setUser(session.user as User)
        fetchUserProfile(session.user.id)
      } else {
        console.log('Auth state changed: user logged out')
        setUser(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchUserProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('Failed to fetch user profile:', error)
        // 使用当前 session 的用户信息
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          setUser(user as User)
        }
        return
      }
      
      if (data) {
        setUser(data as User)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    } finally {
      setLoading(false)
    }
  }

  async function signIn(email: string, password: string) {
    console.log('Signing in with email:', email)
    
    const { data, error } = await supabase.auth.signInWithPassword({ 
      email, 
      password 
    })
    
    if (error) {
      console.error('Sign in error:', error)
      throw error
    }
    
    if (!data?.user) {
      console.error('No user returned from sign in')
      throw new Error('登录失败：未返回用户信息')
    }
    
    // 设置用户状态
    setUser(data.user as User)
    console.log('User set:', data.user.email)
    
    // 等待一小段时间确保状态更新
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // 尝试获取用户资料
    try {
      await fetchUserProfile(data.user.id)
    } catch (e) {
      console.error('Error fetching profile:', e)
    }
  }

  async function signUp(email: string, password: string, username: string, displayName?: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: displayName,
        },
      },
    })
    if (error) throw error
    
    // 创建用户档案
    if (data.user) {
      // 检查是否已有用户
      const { count, error: countError } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
      
      if (countError) {
        console.error('Error checking user count:', countError)
      }
      
      const isFirstUser = !count || count === 0
      console.log('First user:', isFirstUser)
      
      const { error: profileError } = await supabase.from('user_profiles').insert({
        id: data.user.id,
        email,
        username,
        role: isFirstUser ? 'admin' : 'viewer',
        display_name: displayName || username,
        is_active: true,
      })
      
      if (profileError) {
        console.error('Error creating user profile:', profileError)
        throw profileError
      }
      
      console.log('User profile created with role:', isFirstUser ? 'admin' : 'viewer')
    }
  }

  async function verifyEmail(token: string) {
    console.log('Email verification token:', token)
  }

  async function sendVerificationEmail() {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user?.email || '',
    })
    if (error) throw error
  }

  async function resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, verifyEmail, sendVerificationEmail, resetPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
